import { Pdf, SinglePlanSidePanel } from '@/@types/content';
import PanelContentWithErrorBoundary from '@/components/blocks/ErrorFallback/PanelContentWithErrorBoundary';
import ContentArea from '@/components/cms/ContentArea';
import Button from '@/components/ui/Buttons/Button';
import Icon from '@/components/ui/Icon';
import Panel, { PanelProps } from '@/components/ui/Panel';
import useToast from '@/hooks/useToast';
import {
  useLazyDeleteCreditPlanQuery,
  useResendCreditPlanEmailMutation,
  ResendCreditPlanEmailRequest,
  useSubmitCreditPlanFeedbackMutation
} from '@/redux/api/client/creditPlan';
import { updatePlanSavedItems, upsertPlan } from '@/redux/slices/creditPlanSlice';
import { templateString } from '@/utils/helpers';
import classnames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import Divider from '../../Divider';
import DropdownMenu from '../../DropdownMenu';
import CircleButton from '../../Buttons/CircleButton';
import { DEFAULT_AI_POWERED_TEXT } from '../util/constants';
import { getCreditPlanItemSavedState } from '../util/CreditPlanHelpers';
import { selectCartOpenStatus } from '@/redux/selectors/cartSelectors';
import { selectSiteLabels } from '@/redux/selectors/pageSelectors';
import { useAppSelector } from '@/hooks';
import { useGetCreditPlan } from '@/contexts/creditPlan';
import { expandContent } from '@/lib/helpers/expandChildren';
import useCreditPlanPdf from '../Pdf/useCreditPlanPdf';
import type { RichTextBlock as RichTextBlockProps } from '@/@types/content';
import DebugInformation from '../DebugInformation';
import CreditPlanFailureSidePanelContent from './CreditPlanFailureSidePanelContent';
import {
  CreditPlanFailureReason,
  CreditPlanJurisdictionAlertMessage,
  CreditPlanResultItem,
  CreditPlanType,
  ItemAnalyticsModel,
  ItemListAnalyticsModel
} from '@/@types/client-api';
import DebugButtons from '../DebugButtons';
import { IContent } from '@/@types/cms';
import AnnouncementAlertBlock from '@/components/blocks/AnnouncementAlertBlock';
import { useAnalytics } from '@/analytics/hooks/useAnalytics';
import { AnalyticsContext, AnalyticsEventName } from '@/analytics/constants';
import ThumbFeedbackButtons, {
  ThumbFeedbackValue
} from '@/components/ui/Feedback/ThumbFeedbackButtons';
import { trackViewItemListEvent } from '@/analytics/ecommerce';
import PlanMetadata from '../PlanMetadata';
import useCreditPlanLibrary from '../hooks/useCreditPlanLibrary';
import { CreditPlanMiniTrackerBlockName } from '@/components/blocks/CreditPlan/CreditPlanMiniTrackerBlock';
import { parseISO } from 'date-fns';
import CreditPlanSearchResultBlock from '@/components/blocks/CreditPlan/CreditPlanSearchResultBlock';
import CreditPlanPdfDebugView from '../Pdf/CreditPlanPdfDebugView';

export interface GeneratedCreditPlanProps extends Omit<PanelProps, 'size' | 'children'> {
  creditPlanLoadFailureContent?: IContent[];
  jurisdictionId?: string;
  jurisdictionName?: string;
  complianceEndDate?: string;
  flags?: string[];
  aiPoweredText?: React.ReactNode;
  icon?: string;
  content?: SinglePlanSidePanel & {
    alerts?: CreditPlanJurisdictionAlertMessage[];
  };
  pdfContent?: Pdf;
  debugModeEnabled?: boolean;
  onBackButtonPress?: () => void;
}

const GeneratedCreditPlanSidePanel: React.FC<GeneratedCreditPlanProps> = ({
  creditPlanLoadFailureContent,
  jurisdictionName,
  jurisdictionId,
  complianceEndDate,
  aiPoweredText,
  icon,
  pdfContent,
  debugModeEnabled,
  onBackButtonPress,
  content: {
    heading: rawHeading,
    subheading,
    messagingContentArea,
    topMarketingContentArea,
    buttonText,
    alerts
  } = {},
  onOpenChange,
  ...panelProps
}) => {
  const dispatch = useDispatch();
  const [isDeleting, setIsDeleting] = useState<boolean>();
  const [emailResent, setEmailResent] = useState<boolean>(false);
  const [isResendingEmail, setIsResendingEmail] = useState<boolean>(false);
  const [isDownloadingPlan, setIsDownloadingPlan] = useState<boolean>(false);
  const [isPdfPreviewMode, setIsPdfPreviewMode] = useState<boolean>(false);
  const [planItem] = useGetCreditPlan(jurisdictionId);
  const cartPanelIsOpen = useAppSelector(selectCartOpenStatus);
  const siteLabels = useAppSelector(selectSiteLabels);
  const resolvedAiPoweredText =
    aiPoweredText ?? siteLabels?.aiCreditComplainceText ?? DEFAULT_AI_POWERED_TEXT;
  const { trackEvent } = useAnalytics();

  const { showSuccessMessage, showFailMessage } = useToast();

  const [feedbackByPlan, setFeedbackByPlan] = useState<Record<string, ThumbFeedbackValue>>({});
  const [submitCreditPlanFeedback] = useSubmitCreditPlanFeedbackMutation();

  const planId = useMemo(
    () => planItem?.processId ?? planItem?.creditPlan?.id ?? undefined,
    [planItem?.processId, planItem?.creditPlan?.id]
  );

  const [deletePlan] = useLazyDeleteCreditPlanQuery();
  const [resendEmail] = useResendCreditPlanEmailMutation();

  const debugInformation = useMemo(() => planItem?.debugInformation, [planItem?.debugInformation]);
  const plan = useMemo(() => planItem?.creditPlan, [planItem?.creditPlan]);

  const responseFeedbackSent = Boolean(planItem?.responseFeedbackSent);
  const currentPlanFeedback = planId ? (feedbackByPlan[planId] ?? null) : null;
  const isFeedbackDisabled = responseFeedbackSent || Boolean(planId && feedbackByPlan[planId]);
  const resolvedJurisdiction = useMemo(
    () => jurisdictionName || jurisdictionId || 'unknown',
    [jurisdictionId, jurisdictionName]
  );

  const itemListAnalyticsModel = useMemo<ItemListAnalyticsModel>(() => {
    return {
      listId: `${jurisdictionName}`,
      listName: `CreditPlan`
    };
  }, [jurisdictionName]);

  const updateSavedItems = useCallback(
    (records: Record<string, boolean>) => {
      if (!jurisdictionId) return;

      dispatch(updatePlanSavedItems({ jurisdiction: jurisdictionId, items: records }));
    },
    [dispatch, jurisdictionId]
  );

  const {
    handleRemoveAllFromLibrary,
    handleSaveAllToLibrary,
    handleSingleSaveToLibrary,
    handleSingleRemoveFromLibrary,
    savingItems
  } = useCreditPlanLibrary({
    plan: plan ?? {},
    analyticsContext: AnalyticsContext.CreditPlanPanel,
    jurisdictionId,
    resolvedJurisdiction,
    itemListAnalyticsModel,
    onSavedItemsChange: updateSavedItems,
    scopeKey: `${jurisdictionId ?? ''}:${plan?.id ?? ''}`
  });

  const isSaving = useMemo(() => !!Object.values(savingItems).some(x => !!x), [savingItems]);

  const openHotjarCreditPlanSurvey = useCallback(() => {
    if (typeof window === 'undefined') return;

    trackEvent(AnalyticsEventName.FeedbackCreditplan, {
      context: AnalyticsContext.CreditPlanPanel,
      jurisdiction: resolvedJurisdiction
    });

    if (typeof window.hj !== 'function') return;

    window.hj('event', 'creditplan_feedback');
  }, [resolvedJurisdiction, trackEvent]);

  const handleFeedback = useCallback(
    async (value: ThumbFeedbackValue) => {
      if (isFeedbackDisabled || !planId) return;

      trackEvent(AnalyticsEventName.RateCreditplan, {
        context: AnalyticsContext.CreditPlanPanel,
        jurisdiction: resolvedJurisdiction,
        method: value === 'thumbsUp' ? 'Positive' : 'Negative'
      });

      try {
        await submitCreditPlanFeedback({ planId, thumbValue: value }).unwrap();

        setFeedbackByPlan(prev => ({
          ...prev,
          [planId]: value
        }));

        if (planItem && jurisdictionId) {
          dispatch(
            upsertPlan({
              jurisdiction: jurisdictionId,
              plan: {
                ...planItem,
                responseFeedbackSent: true
              }
            })
          );
        }

        if (value === 'thumbsUp') {
          const message = (
            <span className="text-2">
              Response submitted - thank you!{' '}
              <button className="underline" onClick={openHotjarCreditPlanSurvey}>
                Leave feedback
              </button>
            </span>
          );

          showSuccessMessage(message);
        } else {
          showSuccessMessage('Response submitted - thank you!');
          openHotjarCreditPlanSurvey();
        }
      } catch {
        showFailMessage('Failed to send feedback');
      }
    },
    [
      dispatch,
      isFeedbackDisabled,
      jurisdictionId,
      planId,
      planItem,
      resolvedJurisdiction,
      showFailMessage,
      showSuccessMessage,
      submitCreditPlanFeedback,
      trackEvent,
      openHotjarCreditPlanSurvey
    ]
  );

  // Swap side panels to prevent layering
  const isOpen = useMemo(
    () => panelProps.isOpen && !cartPanelIsOpen,
    [cartPanelIsOpen, panelProps.isOpen]
  );

  // Reset email resend states when panel is closed
  useEffect(() => {
    if (!isOpen) {
      setEmailResent(false);
      setIsResendingEmail(false);
    }
  }, [isOpen]);

  const allItemsSaved = useMemo(() => {
    const items = plan?.creditProductGroups?.flatMap(a => a.searchResultItems ?? []) ?? [];
    return items.length > 0 && items.every(item => getCreditPlanItemSavedState(item));
  }, [plan?.creditProductGroups]);

  const flattenedPlanItems = useMemo(
    () => plan?.creditProductGroups?.flatMap(group => group.searchResultItems ?? []) ?? [],
    [plan?.creditProductGroups]
  );

  const getCreditPlanHeadingParts = (rawHeading?: string, jurisdictionName?: string) => {
    const [callout, ...rest] = rawHeading?.split('\n') ?? [];

    const content = templateString(
      rest.join('\n'),
      {
        License: jurisdictionName
      },
      true
    );

    return {
      callout,
      content,
      text: [callout, content].filter(Boolean).join(' ')
    };
  };

  const headingData = useMemo(() => {
    return getCreditPlanHeadingParts(rawHeading, jurisdictionName);
  }, [rawHeading, jurisdictionName]);

  const heading = useMemo(() => {
    return (
      <>
        <span className="bg-gradient-full bg-clip-text text-transparent">
          {headingData.callout}
        </span>
        <br />
        {headingData.content}
      </>
    );
  }, [headingData]);

  const { triggerDownload, PdfDocument, isPdfViewOpen, setIsPdfViewOpen } = useCreditPlanPdf({
    heading: rawHeading ?? '',
    plan: plan ?? {},
    complianceEndDate,
    jurisdictionId: jurisdictionId,
    content: pdfContent,
    betaIcon: !!icon,
    disclaimer: expandContent(messagingContentArea)?.filter(a => !!(a as RichTextBlockProps))?.[0],
    aiPoweredText: resolvedAiPoweredText,
    jurisdictionName,
    subheading
  });

  const _handleSaveAllToLibrary = useCallback(async () => {
    const result = await handleSaveAllToLibrary(CreditPlanType.PERSONALIZED);

    if (result) {
      showSuccessMessage('All programs saved to library');
    } else {
      showFailMessage('Failed to add all programs to library');
    }
  }, [handleSaveAllToLibrary, showFailMessage, showSuccessMessage]);

  const _handleRemoveAllFromLibrary = useCallback(async () => {
    const data = await handleRemoveAllFromLibrary();

    if (data) {
      showSuccessMessage(
        <span className="flex flex-row items-center justify-start">
          <span className="mr-2">All programs removed from library.</span>
          <a
            onClick={async () => await _handleSaveAllToLibrary()}
            href="#"
            className="text-2 underline hover:text-2-bold hover:text-white"
          >
            Undo
          </a>
        </span>
      );
      return;
    } else {
      showFailMessage('Failed to remove all programs from library');
    }
  }, [_handleSaveAllToLibrary, handleRemoveAllFromLibrary, showFailMessage, showSuccessMessage]);

  const _handleSingleSaveToLibrary = useCallback(
    async (item?: CreditPlanResultItem | null) => {
      const result = await handleSingleSaveToLibrary(CreditPlanType.PERSONALIZED, item);

      if (result) showSuccessMessage('Program added to library');
      else showFailMessage('Failed to add program to library');
    },
    [handleSingleSaveToLibrary, showFailMessage, showSuccessMessage]
  );

  const _handleSingleRemoveFromLibrary = useCallback(
    async (item?: CreditPlanResultItem | null) => {
      const result = await handleSingleRemoveFromLibrary(item);

      if (result) {
        showSuccessMessage(
          <span className="flex flex-row items-center justify-start">
            <span className="mr-2">Program removed from library.</span>
            <a
              onClick={async () => await _handleSingleSaveToLibrary(item)}
              href="#"
              className="text-2 underline hover:text-2-bold hover:text-white"
            >
              Undo
            </a>
          </span>
        );
        return;
      } else {
        showFailMessage('Failed to remove program from library');
      }
    },
    [_handleSingleSaveToLibrary, handleSingleRemoveFromLibrary, showFailMessage, showSuccessMessage]
  );

  const deleteCreditPlan = useCallback(async () => {
    const id = planItem?.processId ?? planItem?.creditPlan?.id;

    if (id) {
      setIsDeleting(true);
      const { isError, data } = await deletePlan(id);
      setIsDeleting(false);

      if (!isError && !!data) {
        if (onBackButtonPress) onBackButtonPress();
        else if (onOpenChange) onOpenChange(false);
      } else {
        showFailMessage('Failed to delete plan');
      }
    }
  }, [
    deletePlan,
    onBackButtonPress,
    onOpenChange,
    planItem?.creditPlan?.id,
    planItem?.processId,
    showFailMessage
  ]);

  const menuButtonTrackEvent = useCallback(
    (event: AnalyticsEventName) => {
      trackEvent(event, {
        jurisdiction: resolvedJurisdiction
      });
    },
    [resolvedJurisdiction, trackEvent]
  );

  const handleResendEmail = useCallback(async () => {
    if (emailResent || isResendingEmail) {
      return;
    }

    const processId = planItem?.processId;
    const licenseIdentifier = planItem?.licenseIdentifier ?? jurisdictionId;

    // Check if both are missing
    if (!processId && !licenseIdentifier) {
      showFailMessage('Email not queued. Missing required information.');
      return;
    }

    // Build payload: use processId if available, otherwise use licenseIdentifier
    const payload: ResendCreditPlanEmailRequest = processId
      ? { processId }
      : { processId: '', licenseIdentifier };

    setIsResendingEmail(true);
    const { error, data } = await resendEmail(payload);
    setIsResendingEmail(false);

    if (!error && (data?.status === 'Pending' || !data?.status)) {
      setEmailResent(true);
      menuButtonTrackEvent(AnalyticsEventName.EmailCreditplan);
      showSuccessMessage('Email queued for resend. Check your inbox shortly');
    } else {
      showFailMessage('Failed to queue email. Please try again.');
    }
  }, [
    emailResent,
    isResendingEmail,
    planItem?.processId,
    planItem?.licenseIdentifier,
    jurisdictionId,
    resendEmail,
    showFailMessage,
    menuButtonTrackEvent,
    showSuccessMessage
  ]);

  const handleDownloadPDF = useCallback(async () => {
    setIsDownloadingPlan(true);
    await triggerDownload();
    menuButtonTrackEvent(AnalyticsEventName.DownloadCreditplan);
    setIsDownloadingPlan(false);
  }, [menuButtonTrackEvent, triggerDownload]);

  const saveButton = useMemo(() => {
    const _buttonText = allItemsSaved ? 'Saved to library' : buttonText;
    const callback = allItemsSaved ? _handleRemoveAllFromLibrary : _handleSaveAllToLibrary;
    const icon = allItemsSaved ? 'checkmark' : 'add';
    return (
      <Button
        iconLeft={icon}
        size="large"
        label={_buttonText}
        onClick={callback}
        disabled={!!isSaving || !!isDeleting}
      />
    );
  }, [
    _handleRemoveAllFromLibrary,
    _handleSaveAllToLibrary,
    allItemsSaved,
    buttonText,
    isDeleting,
    isSaving
  ]);

  const dropdownMenu = useMemo(() => {
    return (
      <DropdownMenu
        menuStyle="standalone-dark"
        button={() => (
          <Button
            iconLeft="more-ios"
            size="large"
            aria-label="More options"
            color="outline-black"
          />
        )}
        items={[
          {
            label: emailResent ? 'Email resent' : 'Re-send email',
            onClick: handleResendEmail,
            icon: 'email',
            isLoading: isResendingEmail,
            disabled: emailResent || isResendingEmail
          },
          {
            label: 'Download copy',
            onClick: isPdfPreviewMode ? () => setIsPdfViewOpen(true) : handleDownloadPDF,
            icon: 'download',
            isLoading: isDownloadingPlan,
            disabled: isDownloadingPlan
          }
        ]}
        menuClassName="mb-2 sm:!border-gray"
      />
    );
  }, [
    emailResent,
    handleResendEmail,
    isResendingEmail,
    isPdfPreviewMode,
    handleDownloadPDF,
    isDownloadingPlan,
    setIsPdfViewOpen
  ]);

  const renderedList = useMemo(
    () => (
      <CreditPlanSearchResultBlock
        creditPlanResponseModel={planItem ?? {}}
        itemListAnalyticsModel={itemListAnalyticsModel}
        analyticsContext={AnalyticsContext.CreditPlanPanel}
        handleSingleRemoveFromLibrary={_handleSingleRemoveFromLibrary}
        handleSingleSaveToLibrary={_handleSingleSaveToLibrary}
        itemSavingStatus={savingItems}
        overrideActions={item => {
          const isSavedInLibrary = getCreditPlanItemSavedState(item);
          const isItemSaving = !!savingItems[item.variationPk ?? ''] || isDeleting;

          return (
            <CircleButton
              icon={isSavedInLibrary ? 'checkmark' : 'add'}
              isLoading={isItemSaving}
              aria-current
              label={isSavedInLibrary ? 'Saved to library' : 'Save to library'}
              onClick={
                isSavedInLibrary
                  ? () => _handleSingleRemoveFromLibrary(item)
                  : () => _handleSingleSaveToLibrary(item)
              }
              disabled={isItemSaving}
            />
          );
        }}
      />
    ),
    [
      planItem,
      itemListAnalyticsModel,
      _handleSingleRemoveFromLibrary,
      _handleSingleSaveToLibrary,
      savingItems,
      isDeleting
    ]
  );

  const planAlertMessages = useMemo(() => {
    if (alerts?.length) {
      return (
        <div className="my-6">
          {alerts?.map((a, i) => {
            return (
              <AnnouncementAlertBlock
                key={`${jurisdictionId}-alert-${i}`}
                copy={a.copy ?? ''}
                style={a.style}
              />
            );
          })}
        </div>
      );
    }
  }, [alerts, jurisdictionId]);

  const handleClosePanel = useCallback(() => onOpenChange?.(false), [onOpenChange]);

  const topContentAreas = useMemo(() => {
    return (
      <>
        {expandContent(topMarketingContentArea)?.map((component, i) => {
          const isFirst = i == 0;
          if (component.contentType?.includes(CreditPlanMiniTrackerBlockName)) {
            return (
              <ContentArea
                components={[component]}
                className={classnames({
                  'pt-4': !isFirst
                })}
                key={i}
                componentsProps={{
                  breakdown: plan?.creditBreakdown ?? {},
                  jurisdictionName,
                  jurisdictionKey: jurisdictionId,
                  context: AnalyticsContext.CreditPlanPanel,
                  onPrimaryLinkClick: handleClosePanel,
                  compliancePeriodEndDate: complianceEndDate
                    ? parseISO(complianceEndDate)
                    : undefined
                }}
              />
            );
          }
          return (
            <ContentArea
              components={[component]}
              key={i}
              className={classnames({
                'pt-4': !isFirst
              })}
            />
          );
        })}
      </>
    );
  }, [
    complianceEndDate,
    handleClosePanel,
    jurisdictionId,
    jurisdictionName,
    plan?.creditBreakdown,
    topMarketingContentArea
  ]);

  useEffect(() => {
    if (!isOpen) return;
    const itemsAnalitics = flattenedPlanItems
      .map(resultItem => resultItem.itemAnalyticsModel)
      .filter((item): item is ItemAnalyticsModel => item !== undefined);

    if (itemsAnalitics && itemsAnalitics.length > 0) {
      trackEvent(AnalyticsEventName.LoadCreditplan, {
        context: AnalyticsContext.CreditPlanPanel,
        jurisdiction: resolvedJurisdiction
      });

      trackViewItemListEvent(
        itemListAnalyticsModel.listId || '',
        itemListAnalyticsModel.listName || '',
        itemsAnalitics,
        AnalyticsContext.CreditPlanPanel
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flattenedPlanItems, isOpen, resolvedJurisdiction]);

  return (
    <>
      <Panel
        {...{
          ...panelProps,
          isOpen,
          onOpenChange
        }}
        size="medium"
      >
        <Panel.Content onBackButtonPress={onBackButtonPress}>
          <PanelContentWithErrorBoundary
            fallback={
              <CreditPlanFailureSidePanelContent
                failureReason={CreditPlanFailureReason.GENERAL}
                debugInformation={debugInformation}
                debugModeEnabled={!!debugModeEnabled}
                planId={planItem?.processId ?? undefined}
                planItem={planItem ?? undefined}
                creditPlanLoadFailureContent={creditPlanLoadFailureContent}
              />
            }
          >
            <Panel.Header>
              <div>
                <h3 className="text-2-medium mb-2 flex items-center justify-start gap-1 text-gray-dark">
                  <Icon name="ai-tool" size="small" />
                  <span className="mr-1">{resolvedAiPoweredText}</span>
                  {icon && (
                    <Icon
                      name={icon}
                      size={icon === 'beta' ? 'xx-large' : 'large'}
                      className={classnames(icon === 'beta' && '-my-2')}
                    />
                  )}
                </h3>
                <h2 className="heading-2">{heading}</h2>
                {subheading && (
                  <p className="text-2 mt-5 font-normal">{templateString(subheading, {}, true)}</p>
                )}
              </div>
            </Panel.Header>
            <Panel.Body scrollClassName="!pt-6">
              <>
                {planAlertMessages}
                {topContentAreas}
                {renderedList}
                {!!debugModeEnabled && (
                  <div className="mb-4 mt-12">
                    <Divider color="light" className="w-full" />
                    <DebugInformation {...debugInformation} planId={planItem?.processId ?? ''} />
                  </div>
                )}
                <ThumbFeedbackButtons
                  value={currentPlanFeedback}
                  onChange={handleFeedback}
                  disabled={isFeedbackDisabled}
                  containerClassName="mb-4 mr-auto mt-12 flex items-center gap-2"
                  disabledClassName="cursor-not-allowed opacity-50 grayscale"
                />
                <PlanMetadata
                  content={headingData.content}
                  generatedAt={planItem?.generatedAt ?? ''}
                  planId={planId ?? ''}
                  isBetaMode={planItem?.isBetaMode}
                />
                <ContentArea
                  components={expandContent(messagingContentArea)}
                  propertyName="MessagingContentArea"
                  className="label mb-6 font-medium text-gray-dark"
                />
              </>
            </Panel.Body>

            <Panel.Footer divider="line" className="flex justify-end gap-5">
              <DebugButtons
                allowed={!!debugInformation?.allowed}
                isActive={!!debugModeEnabled}
                planId={planItem?.processId ?? undefined}
                onDeletePress={deleteCreditPlan}
                isDeleting={!!isDeleting || !!isSaving}
                planItem={planItem ?? undefined}
                planType={CreditPlanType.PERSONALIZED}
                onPdfPreviewToggle={setIsPdfPreviewMode}
              />
              {dropdownMenu}
              {saveButton}
            </Panel.Footer>
          </PanelContentWithErrorBoundary>
        </Panel.Content>
      </Panel>
      {!!debugInformation?.allowed && (
        <CreditPlanPdfDebugView
          open={isPdfViewOpen}
          setOpen={setIsPdfViewOpen}
          key={`${planItem?.jurisdiction}-debugpdf`}
        >
          {PdfDocument}
        </CreditPlanPdfDebugView>
      )}
    </>
  );
};

export default GeneratedCreditPlanSidePanel;
